import cx from "classnames";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { HeroButton } from "@gaming/xds-react-js";

import { LoadingSpinner } from "../../components/Spinner/Spinner";
import { TicketTeamAdminData } from "../../models/ticketing";
import { RouteNames } from "../../partnerConfig/routeNames";
import { OnboardingUrls } from "../../partnerConfig/routes";
import { ticketingActions } from "../../redux/actions/ticketingActions";
import {
  getIsTicketingAdmin,
  getTicketingCreatedTeamId,
  getTicketingIsTeamsAdminFetching,
  getTicketingSelectedAccessId,
  getTicketingTeamsAdmin,
  getTicketingTeamsAdminSellerName,
} from "../../redux/selectors/ticketingSelectors";
import { useTicketingStrings } from "../TicketingPage/ticketingStrings";
import { CreateTeamModal } from "./CreateTeamModal";
import * as styles from "./TeamsAdminSection.module.css";

export const TeamsAdminSection: React.FC = () => {
  const strings = useTicketingStrings();
  const dispatch = useDispatch();
  const history = useHistory();
  const accessId = useSelector(getTicketingSelectedAccessId);
  const teams = useSelector(getTicketingTeamsAdmin);
  const sellerName = useSelector(getTicketingTeamsAdminSellerName);
  const isFetching = useSelector(getTicketingIsTeamsAdminFetching);
  const isAdmin = useSelector(getIsTicketingAdmin);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const handleOpenCreateModal = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  const handleToggleStatus = useCallback(
    (teamId: string, activate: boolean) => {
      if (accessId) {
        dispatch(ticketingActions.toggleTeamStatus.start(teamId, activate, accessId));
      }
    },
    [dispatch, accessId]
  );

  const handleNavigateToTeam = useCallback(
    (teamId: string) => {
      const url = OnboardingUrls[RouteNames.TicketingTeamDetail].replace(":teamId", teamId);
      history.push(accessId ? `${url}?accessid=${accessId}` : url);
    },
    [history, accessId]
  );

  // Navigate to team detail page after successful team creation
  const createdTeamId = useSelector(getTicketingCreatedTeamId);
  const prevCreatedTeamId = useRef<string | undefined>(createdTeamId);
  useEffect(() => {
    if (createdTeamId && createdTeamId !== prevCreatedTeamId.current) {
      prevCreatedTeamId.current = createdTeamId;
      setIsCreateModalOpen(false);
      const url = OnboardingUrls[RouteNames.TicketingTeamDetail].replace(":teamId", createdTeamId);
      history.push(
        accessId ? `${url}?accessid=${accessId}&addMembers=true` : `${url}?addMembers=true`
      );
    }
  }, [createdTeamId, history, accessId]);

  const defaultTeam = teams.find(t => t.isDefault);

  return (
    <div className={styles.section}>
      <div className={styles.header}>
        <h2 className={styles.title}>{strings.ticketTeamsAdminTitle}</h2>
        {isAdmin && (
          <HeroButton as="button" className={styles.createButton} onClick={handleOpenCreateModal}>
            {strings.ticketTeamsAdminCreateButton}
          </HeroButton>
        )}
      </div>
      {sellerName && (
        <p className={styles.subtitle}>
          {strings.ticketTeamsAdminSubtitle.replace("{0}", sellerName)}
        </p>
      )}

      {isFetching ? (
        <div className={styles.spinnerContainer}>
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{strings.ticketTeamsAdminColName} ▴</th>
                  <th>{strings.ticketTeamsAdminColMembers}</th>
                  <th>{strings.ticketTeamsAdminColManagers}</th>
                  <th>{strings.ticketTeamsAdminColStatus}</th>
                  <th>{strings.ticketTeamsAdminColCreated}</th>
                </tr>
              </thead>
              <tbody>
                {teams.map(team => (
                  <TeamRow
                    key={team.id}
                    team={team}
                    strings={strings}
                    isAdmin={!!isAdmin}
                    onToggleStatus={handleToggleStatus}
                    onNavigate={handleNavigateToTeam}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {isAdmin && defaultTeam?.name && (
            <div className={styles.infoCallout}>
              <strong>{strings.ticketTeamsAdminDefaultBadge}.</strong>{" "}
              {strings.ticketTeamsAdminDefaultInfo.replace("{0}", defaultTeam.name)}
            </div>
          )}
        </>
      )}

      {isCreateModalOpen && (
        <CreateTeamModal sellerName={sellerName} onClose={handleCloseCreateModal} />
      )}
    </div>
  );
};

type TeamRowProps = {
  team: TicketTeamAdminData;
  strings: ReturnType<typeof useTicketingStrings>;
  isAdmin: boolean;
  onToggleStatus: (teamId: string, activate: boolean) => void;
  onNavigate: (teamId: string) => void;
};

const TeamRow: React.FC<TeamRowProps> = ({ team, strings, onNavigate }) => {
  const managersDisplay =
    team.managers.length > 0 ? team.managers.join(", ") : strings.ticketTeamsAdminNoManagers;

  return (
    <tr>
      <td>
        {team.isDefault ? (
          <>
            <button
              className={styles.teamLink}
              onClick={() => onNavigate(team.id)}
              title={team.name}
            >
              {team.name}
            </button>
            <span className={styles.defaultBadge}>{strings.ticketTeamsAdminDefaultBadge}</span>
          </>
        ) : (
          <button className={styles.teamLink} onClick={() => onNavigate(team.id)} title={team.name}>
            {team.name}
          </button>
        )}
      </td>
      <td>{team.memberCount}</td>
      <td>{managersDisplay}</td>
      <td>
        <span
          className={cx(styles.statusBadge, {
            [styles.statusActive]: team.isActive,
            [styles.statusInactive]: !team.isActive,
          })}
        >
          {team.isActive
            ? strings.ticketTeamsAdminStatusActive
            : strings.ticketTeamsAdminStatusInactive}
        </span>
      </td>
      <td>{team.createdOn}</td>
    </tr>
  );
};
